// Copyright (C) 2026, Lifefinder Systems International AB, all rights reserved.
import React, { useEffect, useState } from 'react';

import { useDevices } from '../../../context/DeviceContext';
import { deviceAPI, lfDeviceAPI } from '../../../utils/apiClient';
import logger from '../../../utils/logger';

interface AddState {
  title: string;
  name: string;
  deveui: string;
  nwk_key: string;
  device_status: 'active' | 'reserve';
}

interface AdminDevice {
  id: string;
  deveui: string;
  title: string;
  text: string | null;
  active: boolean;
}

export const DeviceManagement: React.FC = () => {
  const { fetchDevices } = useDevices();
  const [adminDevices, setAdminDevices] = useState<AdminDevice[]>([]);

  const fetchAdminDevices = async (): Promise<void> => {
    try {
      const data = await lfDeviceAPI.getLfDevices(true);
      const raw: any[] = data?.devices ?? (Array.isArray(data) ? data : []);
      setAdminDevices(
        raw.map((d) => ({
          id: String(d.id),
          deveui: String(d.deveui),
          title: d.description ?? '',
          text: d.name ?? null,
          active: d.status !== 'deactive',
        }))
      );
    } catch (err) {
      logger.error('Failed to fetch admin devices:', err);
    }
  };

  useEffect(() => {
    fetchAdminDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [addState, setAddState] = useState<AddState>({
    title: '',
    name: '',
    deveui: '',
    nwk_key: '',
    device_status: 'active',
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [confirmDeactivateId, setConfirmDeactivateId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const startEdit = (device: AdminDevice): void => {
    setEditingId(device.deveui);
    setEditTitle(device.title);
    setConfirmDeactivateId(null);
  };

  /** Commit edits by calling the API directly to avoid React stale state issues. */
  const saveEdit = async (): Promise<void> => {
    if (!editingId) return;
    setSaving(true);
    try {
      const device = adminDevices.find((d) => d.deveui === editingId);
      if (!device) return;

      if (editTitle !== device.title) {
        await deviceAPI.updateDevice(editingId, { title: editTitle });
        await fetchAdminDevices();
        await fetchDevices();
      }

      setEditingId(null);
    } catch (err) {
      logger.error('Failed to save device edits:', err);
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = (): void => setEditingId(null);

  const handleDeactivate = async (deviceId: string): Promise<void> => {
    setSaving(true);
    try {
      await deviceAPI.updateDevice(deviceId, { active: false });
      await fetchAdminDevices();
      await fetchDevices();
      setConfirmDeactivateId(null);
    } catch (err) {
      logger.error('Failed to deactivate device:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (deviceId: string): Promise<void> => {
    setSaving(true);
    try {
      await deviceAPI.updateDevice(deviceId, { active: true });
      await fetchAdminDevices();
      await fetchDevices();
    } catch (err) {
      logger.error('Failed to activate device:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async (): Promise<void> => {
    if (!addState.title.trim() || !addState.name.trim() || !addState.deveui.trim()) return;
    setSaving(true);
    try {
      await deviceAPI.manualAddDevice({
        title: addState.title,
        name: addState.name || undefined,
        deveui: addState.deveui,
        nwk_key: addState.nwk_key || undefined,
        device_status: addState.device_status,
      });
      await fetchAdminDevices();
      await fetchDevices();
      setAddState({ title: '', name: '', deveui: '', nwk_key: '', device_status: 'active' });
      setShowAddForm(false);
    } catch (err) {
      logger.error('Failed to add device:', err);
    } finally {
      setSaving(false);
    }
  };

  const cellStyle: React.CSSProperties = {
    padding: '0.8vmin 1.2vmin',
    borderBottom: '1px solid #f0f0f0',
    fontSize: 'max(12px, 1.5vmin)',
    verticalAlign: 'middle',
    textAlign: 'left',
  };

  const inputStyle: React.CSSProperties = {
    border: '1px solid #ccc',
    borderRadius: '0.4vmin',
    padding: '0.4vmin 0.7vmin',
    fontSize: 'max(12px, 1.5vmin)',
    width: '100%',
  };

  const actionBtn = (color: string): React.CSSProperties => ({
    background: color,
    color: '#fff',
    border: 'none',
    borderRadius: '0.4vmin',
    padding: '0.4vmin 1vmin',
    cursor: 'pointer',
    fontSize: 'max(11px, 1.4vmin)',
    marginLeft: '0.4vmin',
  });

  const canAdd =
    addState.title.trim().length > 0 &&
    addState.name.trim().length > 0 &&
    addState.deveui.trim().length === 16;

  // Split devices: active ones first, deactivated at the bottom
  const activeDevices = adminDevices.filter((d) => d.active);
  const deactiveDevices = adminDevices.filter((d) => !d.active);

  return (
    <div style={{ padding: '1vmin 2vmin', overflow: 'auto', flex: 1 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5vmin',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 'max(14px, 1.8vmin)' }}>📱 Devices</h3>
        <div style={{ display: 'flex', gap: '1vmin' }}>
          <button
            onClick={() => {
              void fetchAdminDevices();
              void fetchDevices();
            }}
            style={{ ...actionBtn('#607d8b'), padding: '0.5vmin 1.2vmin' }}
            title="Refresh devices"
          >
            🔄 Refresh
          </button>
          <button
            onClick={() => {
              setShowAddForm((v) => !v);
              setConfirmDeactivateId(null);
              setEditingId(null);
            }}
            style={{ ...actionBtn('#4caf50'), padding: '0.5vmin 1.2vmin' }}
          >
            {showAddForm ? '✕ Cancel' : '＋ Add device'}
          </button>
        </div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '30%' }} />
          <col style={{ width: '40%' }} />
          <col style={{ width: '30%' }} />
        </colgroup>
        <thead>
          <tr style={{ background: '#f5f5f5' }}>
            {['Title', 'Description', 'Actions'].map((h, i) => (
              <th
                key={h}
                style={{
                  ...cellStyle,
                  fontWeight: 600,
                  color: '#444',
                  borderBottom: '2px solid #e0e0e0',
                  textAlign: i === 2 ? 'right' : 'left',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {adminDevices.length === 0 ? (
            <tr>
              <td
                colSpan={3}
                style={{ ...cellStyle, color: '#aaa', textAlign: 'center', padding: '3vmin' }}
              >
                No devices found.
              </td>
            </tr>
          ) : (
            <>
              {activeDevices.map((device) => {
                const isEditing = editingId === device.deveui;
                const isConfirmingDeactivate = confirmDeactivateId === device.deveui;
                return (
                  <tr key={device.id} style={{ background: isEditing ? '#e8f5e9' : undefined }}>
                    <td style={cellStyle}>
                      {isEditing ? (
                        <input
                          style={inputStyle}
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                        />
                      ) : (
                        device.title
                      )}
                    </td>

                    <td style={cellStyle}>
                      <span style={{ color: device.text ? undefined : '#bbb' }}>
                        {device.text ?? '—'}
                      </span>
                    </td>

                    <td style={{ ...cellStyle, whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {isEditing ? (
                        <>
                          <button onClick={saveEdit} disabled={saving} style={actionBtn('#4caf50')}>
                            Save
                          </button>
                          <button onClick={cancelEdit} style={actionBtn('#9e9e9e')}>
                            Cancel
                          </button>
                        </>
                      ) : isConfirmingDeactivate ? (
                        <>
                          <span
                            style={{
                              fontSize: 'max(11px, 1.4vmin)',
                              color: '#c62828',
                              marginRight: '0.5vmin',
                            }}
                          >
                            Deactivate?
                          </span>
                          <button
                            onClick={() => handleDeactivate(device.deveui)}
                            disabled={saving}
                            style={actionBtn('#e53935')}
                          >
                            Yes
                          </button>
                          <button
                            onClick={() => setConfirmDeactivateId(null)}
                            style={actionBtn('#9e9e9e')}
                          >
                            No
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEdit(device)} style={actionBtn('#1976d2')}>
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              setConfirmDeactivateId(device.deveui);
                              setEditingId(null);
                            }}
                            style={actionBtn('#e53935')}
                          >
                            Deactivate
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}

              {/* Deactivated devices rendered at the bottom, visually separated */}
              {deactiveDevices.length > 0 && (
                <>
                  <tr>
                    <td
                      colSpan={3}
                      style={{
                        ...cellStyle,
                        paddingTop: '1.5vmin',
                        paddingBottom: '0.4vmin',
                        color: '#999',
                        fontSize: 'max(10px, 1.2vmin)',
                        fontWeight: 600,
                        letterSpacing: '0.05em',
                        borderBottom: '1px solid #e0e0e0',
                        textTransform: 'uppercase',
                      }}
                    >
                      Deactivated
                    </td>
                  </tr>
                  {deactiveDevices.map((device) => (
                    <tr key={device.id} style={{ background: '#f5f5f5', opacity: 0.7 }}>
                      <td style={{ ...cellStyle, color: '#888' }}>{device.title}</td>
                      <td style={cellStyle}>
                        <span style={{ color: '#bbb' }}>{device.text ?? '—'}</span>
                      </td>
                      <td style={{ ...cellStyle, whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <button
                          onClick={() => handleActivate(device.deveui)}
                          disabled={saving}
                          style={actionBtn('#43a047')}
                        >
                          Activate
                        </button>
                      </td>
                    </tr>
                  ))}
                </>
              )}
            </>
          )}
        </tbody>
      </table>

      {showAddForm && (
        <div
          style={{
            marginTop: '2vmin',
            padding: '1.2vmin 2vmin 1.5vmin',
            background: '#f1f8e9',
            border: '1px solid #aed581',
            borderRadius: '0.8vmin',
          }}
        >
          <div
            style={{
              fontWeight: 700,
              marginBottom: '1vmin',
              fontSize: 'max(14px, 1.8vmin)',
              color: '#333',
            }}
          >
            New device
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.8vmin' }}>
            {/* Row 1: Title + Serial Number */}
            <div style={{ display: 'flex', gap: '3vmin', flexWrap: 'wrap' }}>
              <div
                style={{ flex: '1 1 18vmin', display: 'flex', flexDirection: 'column', gap: '3px' }}
              >
                <label
                  style={{
                    fontSize: 'max(12px, 1.4vmin)',
                    color: '#444',
                    fontWeight: 600,
                  }}
                >
                  Title *
                </label>
                <input
                  style={inputStyle}
                  placeholder="Title"
                  value={addState.title}
                  onChange={(e) => setAddState((s) => ({ ...s, title: e.target.value }))}
                />
              </div>
              <div
                style={{ flex: '1 1 18vmin', display: 'flex', flexDirection: 'column', gap: '3px' }}
              >
                <label
                  style={{
                    fontSize: 'max(12px, 1.4vmin)',
                    color: '#444',
                    fontWeight: 600,
                  }}
                >
                  Serial Number *
                </label>
                <input
                  style={inputStyle}
                  placeholder="Serial Number"
                  value={addState.name}
                  onChange={(e) => setAddState((s) => ({ ...s, name: e.target.value }))}
                />
              </div>
            </div>

            {/* Row 2: Device EUI + NWK Key + Status */}
            <div
              style={{ display: 'flex', gap: '3vmin', flexWrap: 'wrap', alignItems: 'flex-end' }}
            >
              <div
                style={{ flex: '1 1 16vmin', display: 'flex', flexDirection: 'column', gap: '3px' }}
              >
                <label
                  style={{
                    fontSize: 'max(12px, 1.4vmin)',
                    color: '#444',
                    fontWeight: 600,
                  }}
                >
                  Device EUI * (16 hex)
                </label>
                <input
                  style={{
                    ...inputStyle,
                    borderColor:
                      addState.deveui && addState.deveui.length !== 16 ? '#e53935' : undefined,
                  }}
                  placeholder="aabbccddeeff0011"
                  value={addState.deveui}
                  onChange={(e) =>
                    setAddState((s) => ({ ...s, deveui: e.target.value.toLowerCase() }))
                  }
                />
              </div>
              <div
                style={{ flex: '2 1 24vmin', display: 'flex', flexDirection: 'column', gap: '3px' }}
              >
                <label
                  style={{
                    fontSize: 'max(12px, 1.4vmin)',
                    color: '#444',
                    fontWeight: 600,
                  }}
                >
                  NWK Key (32 hex, optional)
                </label>
                <input
                  style={{
                    ...inputStyle,
                    borderColor:
                      addState.nwk_key && addState.nwk_key.length !== 32 ? '#e53935' : undefined,
                  }}
                  placeholder="32-character network key"
                  value={addState.nwk_key}
                  onChange={(e) => setAddState((s) => ({ ...s, nwk_key: e.target.value }))}
                />
              </div>
              <div
                style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: '3px' }}
              >
                <label
                  style={{
                    fontSize: 'max(12px, 1.4vmin)',
                    color: '#444',
                    fontWeight: 600,
                  }}
                >
                  Status
                </label>
                <select
                  style={{ ...inputStyle, width: 'auto' }}
                  value={addState.device_status}
                  onChange={(e) =>
                    setAddState((s) => ({
                      ...s,
                      device_status: e.target.value as 'active' | 'reserve',
                    }))
                  }
                >
                  <option value="active">Active</option>
                  <option value="reserve">Reserve</option>
                </select>
              </div>
            </div>

            {/* Add button on its own row, right-aligned */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={handleAdd}
                disabled={saving || !canAdd}
                style={{
                  ...actionBtn('#4caf50'),
                  padding: '0.8vmin 2.5vmin',
                  fontSize: 'max(13px, 1.5vmin)',
                  opacity: canAdd ? 1 : 0.5,
                }}
              >
                Add device
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
